import React, { useState } from 'react';
import { api } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Mail, Phone, Copy, Check, Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const ShareModal = ({ open, onClose, queryId, queryText }) => {
  const [activeTab, setActiveTab] = useState('email');
  const [recipient, setRecipient] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareContent, setShareContent] = useState(null);

  const handleShare = async () => {
    if (!recipient.trim()) {
      toast.error(`Please enter ${activeTab === 'email' ? 'an email address' : 'a phone number'}`);
      return;
    }

    setLoading(true);
    try {
      const response = await api.shareResults(queryId, activeTab, recipient);
      setShareContent(response.data);
      toast.success('Share link generated!');
    } catch (err) {
      toast.error('Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (shareContent?.content) {
      navigator.clipboard.writeText(shareContent.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied to clipboard');
    }
  };

  const handleEmailOpen = () => {
    if (shareContent?.content) {
      const subject = encodeURIComponent('NoSpin-EBM Evidence Summary');
      const body = encodeURIComponent(shareContent.content);
      window.open(`mailto:${recipient}?subject=${subject}&body=${body}`);
    }
  };

  const handleSMSOpen = () => {
    if (shareContent?.content) {
      const body = encodeURIComponent(shareContent.content);
      window.open(`sms:${recipient}?body=${body}`);
    }
  };

  const resetModal = () => {
    setRecipient('');
    setShareContent(null);
    setCopied(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="share-modal">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
              <Share2 className="h-6 w-6 text-sky-600" />
            </div>
          </div>
          <DialogTitle className="text-center">Share Results</DialogTitle>
          <DialogDescription className="text-center">
            Share this evidence summary with a colleague
          </DialogDescription>
        </DialogHeader>

        {!shareContent ? (
          <>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid grid-cols-2 bg-slate-100 rounded-lg p-1">
                <TabsTrigger value="email" className="rounded-md flex items-center gap-1.5">
                  <Mail className="h-4 w-4" />
                  Email
                </TabsTrigger>
                <TabsTrigger value="sms" className="rounded-md flex items-center gap-1.5">
                  <Phone className="h-4 w-4" />
                  SMS
                </TabsTrigger>
              </TabsList>

              <TabsContent value="email" className="mt-4">
                <div className="space-y-4">
                  <div>
                    <Label>Colleague's Email</Label>
                    <Input
                      type="email"
                      placeholder="colleague@hospital.com"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      className="mt-1"
                      data-testid="share-email-input"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="sms" className="mt-4">
                <div className="space-y-4">
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      className="mt-1"
                      data-testid="share-phone-input"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <Button
              onClick={handleShare}
              disabled={loading || !recipient.trim()}
              className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white mt-4"
              data-testid="share-generate-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Share2 className="h-4 w-4 mr-2" />
              )}
              Generate Share Link
            </Button>
          </>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 max-h-48 overflow-y-auto">
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono">
                {shareContent.content}
              </pre>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleCopy}
                variant="outline"
                className="flex-1 rounded-full"
                data-testid="share-copy-btn"
              >
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                onClick={activeTab === 'email' ? handleEmailOpen : handleSMSOpen}
                className="flex-1 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="share-send-btn"
              >
                {activeTab === 'email' ? <Mail className="h-4 w-4 mr-2" /> : <Phone className="h-4 w-4 mr-2" />}
                Open {activeTab === 'email' ? 'Email' : 'SMS'}
              </Button>
            </div>

            <p className="text-xs text-center text-slate-500">
              Your colleague will receive a signup link to join NoSpin-EBM
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
